import { Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  FileText,
  BookUser,
  UserCircle,
  Users,
  Bot,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import type { Role } from "@/types/common";

/**
 * Bottom tab bar for mobile (< 768px). The destinations are role-aware
 * so an Employee never sees a tab that would land them on a forbidden
 * page — the four tabs are the most-used pages for each role:
 *
 *   EMPLOYEE              Dashboard · My Docs · Directory · Profile
 *   MANAGER               Dashboard · My Team · Directory · Profile
 *   HR_ADMIN / SUPER_ADMIN  Dashboard · People · Agents · Profile
 *
 * The full sidebar is still reachable via the hamburger on the TopBar
 * for everything else — this exists purely so thumb-range navigation
 * works without having to open the drawer every time.
 */
interface Tab {
  path: string;
  label: string;
  icon: LucideIcon;
}

const TABS_BY_ROLE: Record<Role, Tab[]> = {
  EMPLOYEE: [
    { path: "/", label: "Home", icon: LayoutDashboard },
    { path: "/my-documents", label: "Docs", icon: FileText },
    { path: "/directory", label: "Directory", icon: BookUser },
    { path: "/my/profile", label: "Profile", icon: UserCircle },
  ],
  MANAGER: [
    { path: "/", label: "Home", icon: LayoutDashboard },
    { path: "/my/team", label: "My Team", icon: Users },
    { path: "/directory", label: "Directory", icon: BookUser },
    { path: "/my/profile", label: "Profile", icon: UserCircle },
  ],
  HR_ADMIN: [
    { path: "/", label: "Home", icon: LayoutDashboard },
    { path: "/employees", label: "People", icon: Users },
    { path: "/agents", label: "Agents", icon: Bot },
    { path: "/my/profile", label: "Profile", icon: UserCircle },
  ],
  SUPER_ADMIN: [
    { path: "/", label: "Home", icon: LayoutDashboard },
    { path: "/employees", label: "People", icon: Users },
    { path: "/agents", label: "Agents", icon: Bot },
    { path: "/my/profile", label: "Profile", icon: UserCircle },
  ],
};

export function MobileNav() {
  const location = useLocation();
  const { user } = useAuth();
  const tabs = user ? TABS_BY_ROLE[user.role] : null;
  if (!tabs) return null;

  return (
    <nav
      aria-label="Mobile primary navigation"
      className={cn(
        "md:hidden fixed bottom-0 left-0 right-0 z-30",
        "h-16 bg-surface/95 backdrop-blur-sm border-t border-border",
        "flex items-stretch justify-around",
        // Safe-area inset for iPhones with a home indicator.
        "pb-[env(safe-area-inset-bottom)]"
      )}
    >
      {tabs.map((tab) => {
        const active =
          tab.path === "/"
            ? location.pathname === "/"
            : location.pathname.startsWith(tab.path);
        const Icon = tab.icon;
        return (
          <Link
            key={tab.path}
            to={tab.path}
            aria-current={active ? "page" : undefined}
            aria-label={tab.label}
            className={cn(
              "flex-1 flex flex-col items-center justify-center gap-0.5",
              "text-[11px] font-medium transition-colors",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-accent",
              active ? "text-accent" : "text-text-muted hover:text-text-primary"
            )}
          >
            <Icon className="h-5 w-5" aria-hidden="true" />
            <span>{tab.label}</span>
          </Link>
        );
      })}
    </nav>
  );
}
